import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link as ILink } from "@ludens-reklame/rubics-v3-sdk/link/types";
import { Content, Layout, Link, Menu, Title, Wrapper } from "./styles.js";
import { Home } from "./pages/Home.js";
import {
  Constrainer,
  HorizontalSpacer,
} from "../../internal-components/Layout.js";
import { Orders } from "./pages/Orders.js";
import { Contact } from "./pages/Contact.js";
import Page from "./pages/Page.js";

interface MenuItem {
  link?: ILink;
}

interface IPage {
  title?: string;
  content?: string;
}

interface Props {
  pages?: IPage[];
  homeText?: string;
  menuItems?: MenuItem[];
  profilerOrganizationId?: string;
}

const generateSlug = (title: string | undefined) =>
  title ? title.toLowerCase().replace(/\s+/g, "-") : "uten-tittel";

export const MyPage: React.FC<Props> = ({
  homeText,
  profilerOrganizationId,
  pages = [],
  menuItems = [],
}) => {
  const [route, setRoute] = useState<string>("");

  const pagesWithSlugs = useMemo(() => {
    return pages.map((page) => ({
      ...page,
      slug: `#${generateSlug(page.title)}`,
    }));
  }, [pages]);

  const currentPage = useMemo(() => {
    if (route === "#hjem") {
      return { title: "Min side", content: homeText || "" };
    }
    return pagesWithSlugs.find((page) => page.slug === route) || {};
  }, [route, homeText, pagesWithSlugs]);

  const routeData = useMemo<{ title: ReactNode; view: ReactNode }>(() => {
    switch (route) {
      case "#ordrer":
        return {
          title: "Dine ordrer",
          view: <Orders />,
        };
      case "#kontakt":
        return {
          title: "Kontakt oss",
          view: <Contact profilerOrganizationId={profilerOrganizationId} />,
        };
      case "#hjem":
      case "":
        return {
          title: "Min side",
          view: <Home text={homeText} />,
        };
      default:
        return {
          title: currentPage.title,
          view: currentPage.content ? (
            <Page key={route} text={currentPage.content} />
          ) : (
            <div />
          ),
        };
    }
  }, [route, currentPage, homeText, profilerOrganizationId]);

  useEffect(() => {
    setRoute(window.location.hash);

    function onHashChange() {
      setRoute(window.location.hash);
    }

    window.addEventListener("hashchange", onHashChange);

    return () => {
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  const menu = (
    <Menu>
      <Link href="#hjem" $active={!route || route === "#hjem"}>
        Min side
      </Link>
      <Link href="#ordrer" $active={route === "#ordrer"}>
        Dine ordrer
      </Link>
      <Link href="#kontakt" $active={route === "#kontakt"}>
        Kontakt oss
      </Link>
      {pagesWithSlugs.map((page, index) => (
        <Link key={index} href={page.slug} $active={route === page.slug}>
          {page.title || "Uten tittel"}
        </Link>
      ))}
      {menuItems
        .filter((i) => !!i.link)
        .map((i, k) => (
          <Link key={k} href={i.link?.url}>
            {i.link?.title}
          </Link>
        ))}
      <Link href="/logg-ut">Logg ut</Link>
    </Menu>
  );

  return (
    <Wrapper>
      <div className="mobile">{menu}</div>
      <HorizontalSpacer>
        <Constrainer>
          <Title>{routeData.title}</Title>
          <Layout>
            <div className="desktop">{menu}</div>
            <Content>{routeData.view}</Content>
          </Layout>
        </Constrainer>
      </HorizontalSpacer>
    </Wrapper>
  );
};
